import React, { useState, useEffect, Fragment } from "react";
import { connect } from "react-redux";
import { createStructuredSelector } from "reselect";
import moment from "moment";
import OrderItem from "components/OrderItem";
import CautionBox from "components/CautionBox";
import FormatDateWithLocale from "components/FormatDateWithLocale";
import { selectUserId } from "store/user/account/selectors";
import { selectProjectDetails } from "store/project/selectors";
import { ORDER_TYPE } from "constants/index";
import { ORDER_STATUS } from "constants/status";
import { useAcceptOrderCalculation, useFormattedMessage, useSideModalScrollToTop } from "hooks";
import { getOrders } from "api";
import NewLoader from "components/NewLoader";

const messages = {
    boMainHeading: "active_sales",
    boHeading: "sales_offers_users",
    boContent: "offer_to_sell",
    boMoreOffers1: "view",
    boSingleMoreOffers2: "more_sales_offer",
    boMoreOffers2: "more_sales_offers",
    soEditWalletMainHeading: "purchase_offer_create",
    boCreateContent: "create_content_for_different_conditions",
    boButtonText: "create_buy_order",
    notEnoughWalletBalance: "not_enough_wallet_balance",
    noActiveOffers: "no_active_offers",
    loadingButtonText: "please_wait_button",
    cantInvestMore: "cant_invest_more",
    cantInvestMoreThan25k: "cant_invest_more_than25k",
    noOpenSharesAvailable: "no_open_shares_available",
};

const BuyOrders = ({
    className = "",
    onCreateButtonClick,
    onAcceptOffer,
    setCloseTitle,
    setOfferAccepted,
    payInAmount,
    getSellOrders = false,
    project,
    userUid,
    backSlideEffect = "",
    allProjects,
    minShareTypeWise,
    maxInvestAmount,
    loadingAmount,
    investmentAmountData,
    isInvestment1to8M = false,
}) => {
    useSideModalScrollToTop();

    const { formatMessage } = useFormattedMessage();

    const [isSellOrdersLoading, setSellOrdersLoading] = useState(false);
    const [sellOrders, setSellOrders] = useState([]);
    const [orderLimit, setOrderLimit] = useState(3);

    const projectID = project?.projectID;

    useEffect(() => {
        setCloseTitle(formatMessage(messages.boMainHeading));
        setOfferAccepted(false);
    }, []);

    async function fetchSellOrders(type) {
        try {
            setSellOrdersLoading(true);
            const response = await getOrders({ projectID, type });
            response && setSellOrdersLoading(false);
            if (response?.data?.data?.orders?.orders) {
                const filterOrders = response.data.data.orders.orders.filter(
                    (x) => x.LinkSeller !== userUid && x.StatusOrder === ORDER_STATUS.ACTIVE
                );
                setSellOrders(filterOrders.sort((a, b) => parseFloat(a.SharePrice) - parseFloat(b.SharePrice)));
            }
        } catch (error) {
            /* error */
        }
    }

    useEffect(() => {
        getSellOrders && fetchSellOrders(ORDER_TYPE.SELL);
    }, [getSellOrders]);

    function checkCanInvestMore(investedAmount, order, maxInvestAmount) {
        // eslint-disable-next-line react-hooks/rules-of-hooks
        return useAcceptOrderCalculation(investedAmount, order, maxInvestAmount);
    }

    const singleProject = (id) => allProjects?.find((item) => item.projectID === id) || {};

    const exceededTheInvestmentLimit = isInvestment1to8M ? investmentAmountData >= maxInvestAmount : false;

    return (
        <>
            <div className={`buy-orders-container ${className} ${backSlideEffect}`}>
                <h3>{formatMessage(messages.boHeading)}</h3>
                <p>{formatMessage(messages.boContent)}</p>
                <form method="post">
                    <div className="custom-checks buy-orders">
                        {exceededTheInvestmentLimit ? ( //if user has already exceed the limit.
                            <span className="text-12 alert-red-color">{formatMessage(messages.cantInvestMoreThan25k)}</span>
                        ) : isSellOrdersLoading ? (
                            <NewLoader type="primary" className="center" />
                        ) : Array.isArray(sellOrders) && sellOrders.length > 0 ? (
                            sellOrders
                                .slice(0, orderLimit)
                                .map(
                                    (
                                        {
                                            uid,
                                            Shares,
                                            AvailableShares,
                                            SharePrice,
                                            Amount,
                                            IsSplitAllowed,
                                            ExpirationDate,
                                            LinkProduct,
                                            UserCountry,
                                            TypeOrder,
                                            IsTimeoutExtended,
                                            SoldShares,
                                            SharesInProgress,
                                        },
                                        index
                                    ) => {
                                        //1 TO 8 MILLION ONLY: Can user invest more into this project .
                                        const canInvestMore = isInvestment1to8M
                                            ? checkCanInvestMore(
                                                  investmentAmountData,
                                                  {
                                                      AvailableShares,
                                                      SharePrice,
                                                      Amount,
                                                      IsSplitAllowed,
                                                  },
                                                  maxInvestAmount
                                              )
                                            : true;

                                        const project = singleProject(LinkProduct?.ProductID);
                                        const availableShares = parseFloat(AvailableShares || 0);
                                        const isDisable =
                                            !canInvestMore ||
                                            (IsSplitAllowed === "true"
                                                ? !availableShares || availableShares >= minShareTypeWise * 2
                                                    ? parseInt(payInAmount) < minShareTypeWise * parseFloat(SharePrice)
                                                    : parseInt(payInAmount) < availableShares * parseFloat(SharePrice)
                                                : parseInt(Amount) > payInAmount);

                                        return (
                                            <Fragment key={index}>
                                                <OrderItem
                                                    key={index}
                                                    equalTitle={false}
                                                    className={isDisable ? "disable" : ""}
                                                    orderFlag={UserCountry}
                                                    tag={
                                                        IsSplitAllowed === "true"
                                                            ? `split-${TypeOrder.replace(ORDER_TYPE.SELL, ORDER_TYPE.BUY)}`
                                                            : TypeOrder.replace(ORDER_TYPE.SELL, ORDER_TYPE.BUY)
                                                    }
                                                    onClickModal={() => {
                                                        if (!isDisable) {
                                                            onAcceptOffer({
                                                                uid,
                                                                flag: UserCountry,
                                                                typeOrder: TypeOrder,
                                                                tag:
                                                                    IsSplitAllowed === "true"
                                                                        ? `split-${TypeOrder.replace(
                                                                              ORDER_TYPE.SELL,
                                                                              ORDER_TYPE.BUY
                                                                          )}`
                                                                        : TypeOrder.replace(ORDER_TYPE.SELL, ORDER_TYPE.BUY),
                                                                split: !!(IsSplitAllowed === "true" && availableShares >= 1000),
                                                                image: project?.images?.teaserImage?.path,
                                                                title: project?.title,
                                                                city: project?.address?.city,
                                                                shares: AvailableShares,
                                                                price: parseFloat(Amount),
                                                                date: moment(new Date(ExpirationDate?.split("_")[0])).format(
                                                                    "YYYY-MM-DD"
                                                                ),
                                                                pricePerShare: SharePrice,
                                                                isTimeoutExtended: IsTimeoutExtended,
                                                            });
                                                        }
                                                    }}
                                                    isOrderSplit={IsSplitAllowed === "true"}
                                                    split={!!(IsSplitAllowed === "true" && availableShares >= 1000)}
                                                    image={project?.images?.teaserImage?.path}
                                                    title={project?.title}
                                                    // city={project?.address?.city}
                                                    shares={Shares}
                                                    remainingShares={AvailableShares}
                                                    soldShares={IsSplitAllowed === "true" ? SoldShares : null}
                                                    reservedShares={IsSplitAllowed === "true" ? SharesInProgress : null}
                                                    price={parseFloat(Amount)}
                                                    date={<FormatDateWithLocale date={ExpirationDate?.split("_")[0]} />}
                                                    perSharePrice={SharePrice}
                                                />
                                                {isDisable ? (
                                                    <span className="text-12 alert-red-color">
                                                        {formatMessage(
                                                            !availableShares
                                                                ? messages.noOpenSharesAvailable
                                                                : !canInvestMore
                                                                ? messages.cantInvestMore
                                                                : messages.notEnoughWalletBalance
                                                        )}
                                                    </span>
                                                ) : null}
                                            </Fragment>
                                        );
                                    }
                                )
                        ) : (
                            <CautionBox text={formatMessage(messages.noActiveOffers)} />
                        )}
                    </div>
                    {!isSellOrdersLoading && sellOrders?.length > orderLimit ? (
                        <span
                            onClick={() => setOrderLimit(sellOrders.length)}
                            className="text-14 fw-600 load-more secondary-light-color"
                        >{`${formatMessage(messages.boMoreOffers1)} ${sellOrders.length - orderLimit} ${
                            sellOrders.length - orderLimit === 1
                                ? formatMessage(messages.boSingleMoreOffers2)
                                : formatMessage(messages.boMoreOffers2)
                        }`}</span>
                    ) : null}
                </form>
                <div className="border-x my-10" />
                <div className="create-buy-order">
                    <h4 className="mt-0">{formatMessage(messages.soEditWalletMainHeading)}</h4>
                    <p>{formatMessage(messages.boCreateContent)}</p>
                    <button
                        className={`mw-100 mt-10 ${loadingAmount || exceededTheInvestmentLimit ? "disable-grey" : ""}`}
                        onClick={!exceededTheInvestmentLimit && onCreateButtonClick}
                        disabled={loadingAmount || exceededTheInvestmentLimit}
                    >
                        {formatMessage(loadingAmount ? messages.loadingButtonText : messages.boButtonText)}
                        {loadingAmount ? <NewLoader /> : null}
                    </button>
                </div>
            </div>
        </>
    );
};

const mapStateToProps = createStructuredSelector({
    userUid: selectUserId,
    allProjects: selectProjectDetails,
});

export default connect(mapStateToProps)(BuyOrders);
